import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Eye, ArrowLeft } from "lucide-react";
import Button from "../ui/Button";
import QuestionInput from "./QuestionInput";
import CardLayout from "./CardLayout";
import { useQuestionnaireStore } from "../../store/useQuestionnaireStore";
import InputField from "../ui/InputField";

type AnswersState = Record<string, string | string[]>;

const PublicQuestionnaire: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const {
    publicQuestionnaire,
    fetchPublicQuestionnaire,
    submitPublicQuestionnaire,
    isFetching,
    isSubmitting,
  } = useQuestionnaireStore();

  const [clientInfo, setClientInfo] = useState({
    respondentName: "",
    respondentEmail: "",
    respondentPhone: ""
  });

  const [answers, setAnswers] = useState<AnswersState>({});
  const [submitted, setSubmitted] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (token) fetchPublicQuestionnaire(token);
  }, [token, fetchPublicQuestionnaire]);

  useEffect(() => {
    if (publicQuestionnaire) {
      const initial: AnswersState = {};
      publicQuestionnaire.questions.forEach(q => {
        initial[q._id] = q.answerType === "checkbox" ? [] : "";
      });
      setAnswers(initial);
    }
  }, [publicQuestionnaire]);

  const handleChange = (id: string, val: string | string[]) => {
    setAnswers((prev) => ({ ...prev, [id]: val }));
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const handleSubmit = async () => {
    if (!token) return;

    const ok = await submitPublicQuestionnaire(token, {
      respondentName: clientInfo.respondentName,
      respondentEmail: clientInfo.respondentEmail,
      respondentPhone: clientInfo.respondentPhone,
      answers
    });

    // Only show the thank-you screen if it actually saved — the server rejects
    // missing contact details or unanswered required questions.
    if (ok) setSubmitted(true);
  };

  if (isFetching)
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <Loader2 className="animate-spin text-[#2462b2]" size={40} />
        <p className="text-gray-500 font-medium">Loading questionnaire...</p>
      </div>
    );

  if (!publicQuestionnaire)
    return <p className="text-center py-16 text-gray-500">Questionnaire not found.</p>;

  if (submitted) {
    const b: any = (publicQuestionnaire as any).brand || null;
    const color = b?.primaryColor || "#0058c3";
    // Tracking hook: the URL flips to ?submitted=1 so analytics/pixels can
    // record a successful questionnaire submission event.
    if (!window.location.search.includes("submitted=1")) {
      window.history.replaceState({}, "", `${window.location.pathname}?submitted=1`);
    }
    return (
      <div className="min-h-screen w-full flex items-center justify-center p-6 bg-slate-50" data-questionnaire-submitted="true">
        <div className="w-full max-w-lg bg-white shadow-xl rounded-2xl p-10 text-center">
          {b && (
            <div className="flex items-center justify-center mb-6">
              {b.logoUrl
                ? <img src={b.logoUrl} alt={b.companyName} className="h-10 object-contain" />
                : <span className="text-2xl font-extrabold" style={{ color }}>{b?.companyName}</span>}
            </div>
          )}
          <div className="w-16 h-16 rounded-full grid place-items-center mx-auto mb-5" style={{ background: `${color}1a` }}>
            <svg viewBox="0 0 24 24" className="w-9 h-9" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900 mb-2">Thank you — we've got it!</h1>
          <p className="text-[14px] text-slate-600 leading-relaxed">
            Your answers were submitted successfully{b ? ` to ${b.companyName}` : ""}. Our team is reviewing them now
            and will reach out with the next steps shortly.
          </p>
          <div className="mt-6 rounded-xl bg-slate-50 border border-slate-100 p-4 text-left">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">What happens next</p>
            <ul className="text-[13px] text-slate-600 space-y-1">
              <li>1. Your project team reviews your answers</li>
              <li>2. We prepare your project plan &amp; milestones</li>
              <li>3. You'll receive an update by email</li>
            </ul>
          </div>
          {b?.supportEmail && (
            <p className="text-[12px] text-slate-400 mt-5">
              Questions? <a href={`mailto:${b.supportEmail}`} className="font-semibold" style={{ color }}>{b.supportEmail}</a>
            </p>
          )}
        </div>
      </div>
    );
  }

  // Card Layout — a step-by-step wizard (contact → one question per card →
  // review → submit). It has its own built-in review, so the separate
  // preview screen below is only used by the Form layout.
  const brand: any = (publicQuestionnaire as any).brand || null;
  if (publicQuestionnaire.layoutType === "card") {
    return (
      <div className="w-full px-4 py-12 sm:py-16">
        {brand && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {brand.logoUrl
              ? <img src={brand.logoUrl} alt={brand.companyName} className="h-9 object-contain" />
              : <span className="text-xl font-extrabold" style={{ color: brand.primaryColor }}>{brand.companyName}</span>}
          </div>
        )}
        <CardLayout
          name={publicQuestionnaire.name}
          description={publicQuestionnaire.description}
          questions={publicQuestionnaire.questions as any}
          answers={answers}
          onChange={handleChange}
          clientInfo={clientInfo}
          setClientInfo={setClientInfo}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      </div>
    );
  }

  if (showPreview) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-20 space-y-4">
        <div className="w-full max-w-5xl mx-auto p-8 bg-white shadow-2xl rounded-xl border-2 border-blue-200">
          <div className="mb-6 pb-6 border-b-2 border-blue-100">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold text-gray-900">Review Your Responses</h1>
              <button
                onClick={() => setShowPreview(false)}
                className="inline-flex items-center gap-2 text-[#2462b2] font-semibold hover:underline"
              >
                <ArrowLeft size={20} />
                Edit Answers
              </button>
            </div>
            <p className="text-gray-600">Please review your answers before submitting</p>
          </div>

          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-bold text-gray-800 mb-3">Your Information</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 font-medium">Name:</span>
                <span className="text-sm font-bold text-gray-900">{clientInfo.respondentName}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 font-medium">Email:</span>
                <span className="text-sm font-bold text-gray-900">{clientInfo.respondentEmail}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 font-medium">Phone:</span>
                <span className="text-sm font-bold text-gray-900">{clientInfo.respondentPhone}</span>
              </div>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            {publicQuestionnaire.questions.map((q, idx) => {
              const answer = answers[q._id];
              const hasAnswer = Array.isArray(answer) ? answer.length > 0 : answer !== "" && answer !== null && answer !== undefined;

              return (
                <div key={q._id} className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                  <div className="flex items-start justify-between mb-3">
                    <label className="font-bold text-gray-900 text-lg flex-1">
                      <span className="inline-block w-8 h-8 bg-gradient-to-r from-[#2462b2] to-[#1e55a0] text-white rounded-full text-center leading-8 mr-3 text-sm">
                        {idx + 1}
                      </span>
                      {q.question}
                      {q.required && <span className="text-red-500 ml-2">*</span>}
                    </label>
                  </div>

                  <div className="bg-white rounded-lg p-4 border-2 border-blue-100">
                    <p className="text-xs text-gray-500 font-medium mb-2 uppercase">Your Answer:</p>
                    {hasAnswer ? (
                      <QuestionInput
                        question={q}
                        value={answer ?? (q.answerType === "checkbox" ? [] : "")}
                        onChange={() => {}}
                        disabled={true}
                      />
                    ) : (
                      <p className="text-gray-400 italic text-sm">No answer provided</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => setShowPreview(false)}
              className="flex-1 bg-white border-2 border-gray-300 text-gray-700 font-bold py-3 rounded-xl hover:bg-gray-50 transition-all"
            >
              Go Back & Edit
            </button>
            <Button onClick={handleSubmit} isLoading={isSubmitting} className="flex-1">
              {isSubmitting ? "Submitting..." : "Confirm & Submit"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Form layout — a single branded page: contact details, then the project
  // questions, numbered and spaced so a client can work through them quickly.
  const accent = brand?.primaryColor || "#0058c3";
  const answeredCount = publicQuestionnaire.questions.filter((q) => {
    const v = answers[q._id];
    return Array.isArray(v) ? v.length > 0 : String(v ?? "").trim() !== "";
  }).length;
  const totalCount = publicQuestionnaire.questions.length;
  const progress = totalCount ? Math.round((answeredCount / totalCount) * 100) : 0;

  return (
    <div className="min-h-screen w-full bg-slate-50 py-8 sm:py-12 px-4">
      <div className="w-full max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl shadow-[0_10px_40px_-12px_rgba(15,23,42,0.15)] border border-slate-200/70 overflow-hidden">
          {/* Brand header */}
          <div className="px-6 sm:px-10 pt-8 pb-6 border-b border-slate-100">
            {brand && (
              <div className="mb-6">
                {brand.logoUrl ? (
                  <img src={brand.logoUrl} alt={brand.companyName} className="h-10 object-contain" />
                ) : (
                  <span className="text-xl font-extrabold" style={{ color: accent }}>{brand.companyName}</span>
                )}
              </div>
            )}
            <h1 className="text-[26px] sm:text-[30px] font-extrabold text-slate-900 tracking-tight leading-tight">
              {publicQuestionnaire.name}
            </h1>
            {publicQuestionnaire.description && (
              <p className="text-[14px] text-slate-500 mt-2 leading-relaxed">{publicQuestionnaire.description}</p>
            )}
            <div className="mt-5 flex items-center gap-3">
              <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-300" style={{ width: `${progress}%`, background: accent }} />
              </div>
              <span className="text-[12px] font-semibold text-slate-400 shrink-0">
                {answeredCount} of {totalCount} answered
              </span>
            </div>
          </div>

          <div className="px-6 sm:px-10 py-8 space-y-9">
            {/* Your details */}
            <section>
              <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 mb-4">Your Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <InputField
                    label="Full Name"
                    placeholder="Enter your full name"
                    value={clientInfo.respondentName}
                    required
                    onChange={(e) => setClientInfo((p) => ({ ...p, respondentName: e.target.value }))}
                  />
                </div>
                <InputField
                  label="Email Address"
                  type="email"
                  placeholder="you@company.com"
                  value={clientInfo.respondentEmail}
                  required
                  onChange={(e) => setClientInfo((p) => ({ ...p, respondentEmail: e.target.value }))}
                />
                <InputField
                  label="Phone Number"
                  type="tel"
                  placeholder="+1 555 000 0000"
                  value={clientInfo.respondentPhone}
                  required
                  onChange={(e) => setClientInfo((p) => ({ ...p, respondentPhone: e.target.value }))}
                />
              </div>
            </section>

            {/* Project questions — numbered, each in its own block */}
            <section>
              <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 mb-4">Project Questions</h2>
              <div className="space-y-6">
                {(() => {
                  // ── Which questions apply right now ──────────────────────
                  //
                  // A question can declare that it only applies when an earlier
                  // answer matched. The print intake relies on this: a client
                  // who did not ask for a brochure is never shown the fold
                  // format, the panel count or the cover headline.
                  //
                  // Resolved on every render rather than stored, so unticking a
                  // piece immediately withdraws its questions instead of leaving
                  // orphaned answers behind.
                  const byKey: Record<string, any> = {};
                  for (const q of publicQuestionnaire.questions) {
                    if (q.key) byKey[q.key] = answers[q._id];
                  }

                  const applies = (q: any) => {
                    const w = q.showWhen;
                    if (!w?.questionKey || !w.anyOf?.length) return true;
                    const given = byKey[w.questionKey];
                    const list = Array.isArray(given) ? given : given ? [given] : [];
                    return w.anyOf.some((v: string) => list.includes(v));
                  };

                  const visible = publicQuestionnaire.questions.filter(applies);

                  // Numbering follows what is actually on screen, so a client
                  // who skipped the brochure does not see a gap where its
                  // questions would have been.
                  let n = 0;
                  let lastSection = "";

                  return visible.map((q: any) => {
                    n += 1;
                    const newSection = q.section && q.section !== lastSection;
                    if (q.section) lastSection = q.section;

                    return (
                      <div key={q._id}>
                        {newSection && (
                          <div className="pt-4 pb-1 first:pt-0">
                            <div
                              className="text-[11px] font-bold uppercase tracking-wider mb-1"
                              style={{ color: accent }}
                            >
                              {q.section}
                            </div>
                            {q.sectionTitle && (
                              <h3 className="text-[18px] font-bold text-slate-800 leading-snug">
                                {q.sectionTitle}
                              </h3>
                            )}
                            {q.sectionIntro && (
                              <p className="text-[13.5px] text-slate-500 mt-1 leading-relaxed">
                                {q.sectionIntro}
                              </p>
                            )}
                            <div className="h-px bg-slate-100 mt-3" />
                          </div>
                        )}

                        <div className="flex gap-3.5 mt-4">
                          <span
                            className="shrink-0 w-7 h-7 rounded-full grid place-items-center text-[12px] font-bold mt-0.5"
                            style={{ background: `${accent}14`, color: accent }}
                          >
                            {n}
                          </span>
                          <div className="flex-1 min-w-0">
                            <label className="block text-[15px] font-semibold text-slate-800 leading-snug mb-1">
                              {q.question}
                              {q.required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            {q.helpText && (
                              <p className="text-[12.5px] text-slate-500 mb-2 leading-relaxed">{q.helpText}</p>
                            )}
                            {!q.helpText && <div className="mb-2" />}
                            <QuestionInput
                              question={q}
                              value={
                                answers[q._id] ??
                                (["checkbox", "multiselect", "cards-multi", "slider-pairs"].includes(q.answerType)
                                  ? []
                                  : "")
                              }
                              onChange={(val) => handleChange(q._id, val)}
                              required={q.required}
                              accent={accent}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </section>
          </div>

          {/* Actions */}
          <div className="px-6 sm:px-10 py-5 bg-slate-50/80 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handlePreview}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-white border-2 font-bold py-3 rounded-xl transition-all hover:text-white"
              style={{ borderColor: accent, color: accent }}
              onMouseEnter={(e) => (e.currentTarget.style.background = accent)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
            >
              <Eye size={18} />
              Preview Answers
            </button>
            <Button onClick={handleSubmit} isLoading={isSubmitting} className="flex-1">
              {isSubmitting ? "Submitting..." : "Submit Directly"}
            </Button>
          </div>
        </div>

        {brand && (
          <p className="text-center text-[12px] text-slate-400 mt-5">
            {brand.companyName}
            {brand.supportEmail && (
              <>
                {" · "}
                <a href={`mailto:${brand.supportEmail}`} className="font-semibold" style={{ color: accent }}>
                  {brand.supportEmail}
                </a>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default PublicQuestionnaire;
